/**
 * CASL Converter Utility
 * 
 * Converts Gobodo Role permissions to CASL abilities.
 * Part of PE-1021: Convert Gobodo abilities to CASL
 */

import { AbilityBuilder, PureAbility } from '@casl/ability';
import { Role, ObjectPermission, SearchPermissions } from '../services/providers/roleProvider';
import { AppAbility, Subject } from '../types/casl';

/**
 * Map object IDs/names to CASL Subject types
 */
function mapObjectToSubject(objectId: string, objectName: string): Subject | null {
  // Normalize object name
  const normalized = objectName.toLowerCase().trim();
  
  const objectMap: Record<string, Subject> = {
    'client': 'Client',
    'case': 'Case',
    'user': 'User',
    'contact': 'Contact',
    'lead': 'Lead',
    'opportunity': 'Opportunity',
    'event': 'Event',
    'task': 'Task',
    'report': 'Report',
    'account': 'Account',
    // CyberSweep
    'cybersweep_client': 'CyberSweepClient',
    'cybersweepclient': 'CyberSweepClient',
    'csaudit': 'CSAudit',
    'audit': 'CSAudit',
    'pawrecord': 'PAWRecord',
    'paw_record': 'PAWRecord',
    // Credit Block
    'creditblock': 'CreditBlock',
    'credit_block': 'CreditBlock',
    'credit_bureaus': 'Credit_Bureaus',
    'bureaus': 'Credit_Bureaus',
    'unlock_request': 'Unlock_Request',
  };
  
  if (objectMap[normalized]) {
    return objectMap[normalized];
  }
  
  // Try objectId pattern matching (e.g. obj_cs_audit -> cs_audit)
  if (objectId.startsWith('obj_')) {
    const objType = objectId.replace('obj_', '').toLowerCase();
    if (objectMap[objType]) {
      return objectMap[objType];
    }
  }
  
  // Fallback: try capitalized name against known subjects
  const capitalized = normalized.charAt(0).toUpperCase() + normalized.slice(1);
  const knownSubjects = [
    'Client', 'Case', 'User', 'Contact', 'Lead', 'Opportunity', 'Event',
    'Task', 'Report', 'Account', 'CyberSweepClient', 'CSAudit', 'PAWRecord',
    'CreditBlock', 'Credit_Bureaus', 'Unlock_Request',
  ];
  if (knownSubjects.includes(capitalized)) {
    return capitalized as Subject;
  }
  
  return 'Object';
}

/**
 * Convert ObjectPermission array to CASL rules
 */
function convertObjectPermissions(
  permissions: ObjectPermission[],
  builder: AbilityBuilder<AppAbility>
): void {
  for (const perm of permissions) {
    const subject = mapObjectToSubject(perm.objectId, perm.objectName);
    if (!subject) continue;
    
    if (perm.read) {
      builder.can('read', subject);
    }
    if (perm.write) {
      builder.can('write', subject);
      builder.can('update', subject);
      builder.can('create', subject);
    }
    if (perm.delete) {
      builder.can('delete', subject);
    }
  }
}

/**
 * Convert SearchPermissions to CASL rules
 */
function convertSearchPermissions(
  searchPerms: SearchPermissions,
  builder: AbilityBuilder<AppAbility>
): void {
  // Object search permissions
  if (searchPerms.canSearchObjects) {
    builder.can('search', 'Object');
  }
  if (searchPerms.canSearchFields) {
    builder.can('search', 'Field');
  }
  if (searchPerms.canSearchWorkflows) {
    builder.can('search', 'Workflow');
  }
  if (searchPerms.canSearchReports) {
    builder.can('search', 'Report');
  }
  if (searchPerms.canSearchDashboards) {
    builder.can('search', 'Dashboard');
  }
  
  // Specific object search permissions
  if (searchPerms.canSearchClients) {
    builder.can('search', 'Client');
  }
  if (searchPerms.canSearchContacts) {
    builder.can('search', 'Contact');
  }
  if (searchPerms.canSearchLeads) {
    builder.can('search', 'Lead');
  }
  if (searchPerms.canSearchOpportunities) {
    builder.can('search', 'Opportunity');
  }
  if (searchPerms.canSearchCases) {
    builder.can('search', 'Case');
  }
  
  // App-specific search permissions
  if (searchPerms.canSearchCyberSweepClients || searchPerms.canSearchCSAudits) {
    builder.can('search', 'cybersweep');
  }
  if (searchPerms.canSearchCreditBlockAccounts) {
    builder.can('search', 'credit_block');
  }
  if (searchPerms.canSearchCreditCases || searchPerms.canSearchTradelines) {
    builder.can('search', 'credit_management');
  }
  
  // Admin/search permissions
  if (searchPerms.canSearchSystemSettings) {
    builder.can('access', 'Settings');
  }
  if (searchPerms.canSearchUsers) {
    builder.can('search', 'User');
  }
  if (searchPerms.canSearchRoles) {
    builder.can('search', 'Role');
  }
  if (searchPerms.canSearchSecurity) {
    builder.can('access', 'Admin');
  }
  
  // Profile settings (usually available to all)
  if (searchPerms.canSearchProfileSettings !== false) {
    builder.can('access', 'Settings');
  }
}

/**
 * Convert app access permissions based on search permissions
 */
function convertAppPermissions(
  searchPerms: SearchPermissions | undefined,
  builder: AbilityBuilder<AppAbility>
): void {
  if (!searchPerms) return;
  
  // Always allow access to main app
  builder.can('access', 'regal_main');
  
  // App-specific access based on search permissions
  if (searchPerms.canSearchCyberSweepClients || searchPerms.canSearchCSAudits) {
    builder.can('access', 'cybersweep');
  }
  if (searchPerms.canSearchCreditBlockAccounts) {
    builder.can('access', 'credit_block');
  }
  if (searchPerms.canSearchCreditCases || searchPerms.canSearchTradelines) {
    builder.can('access', 'credit_management');
  }
}

/**
 * Convert a Role to CASL Ability
 * 
 * @param role - The role to convert
 * @returns CASL Ability instance
 */
export function roleToCaslAbility(role: Role | null): AppAbility {
  return rolesToCaslAbility(role ? [role] : []);
}

/**
 * Convert multiple Roles to a single merged CASL Ability.
 * Rules are unioned, and any system role short-circuits to `manage all`.
 */
export function rolesToCaslAbility(roles: Role[] | null | undefined): AppAbility {
  const builder = new AbilityBuilder<AppAbility>(PureAbility);
  const { can, build } = builder;
  const safeRoles = roles ?? [];
  
  // Handle null/empty roles - minimal read-only access
  if (safeRoles.length === 0) {
    can('read', 'Client');
    can('access', 'regal_main');
    return build();
  }
  
  // Any system admin role gets full access
  if (safeRoles.some((role) => role.isSystem || role.name === 'System Administrator')) {
    can('manage', 'all');
    return build();
  }

  for (const role of safeRoles) {
    if (role.permissions && role.permissions.length > 0) {
      convertObjectPermissions(role.permissions, builder);
    }
    if (role.searchPermissions) {
      convertSearchPermissions(role.searchPermissions, builder);
      convertAppPermissions(role.searchPermissions, builder);
    }
  }
  
  can('access', 'regal_main');
  
  return build();
}

/**
 * Create minimal ability (read-only, no role)
 */
export function createMinimalAbility(): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(PureAbility);
  can('read', 'Client');
  can('access', 'regal_main');
  return build();
}

/**
 * Create system admin ability (full access)
 */
export function createSystemAdminAbility(): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(PureAbility);
  can('manage', 'all');
  return build();
}
